import { useEffect, useRef, useState } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { FileImport } from './FileImport';
import { UMFormStyles, UMR, UMSelect } from '../../urbanmap/_components';
import type { PlanGeoData } from '../proposition-c/types';
import { MapPane, type MapOverlay } from './MapPane';
import { demoPoints, parseTechnicalPoints, pointObstacles, validatePoint } from './technicalPoints';
import { labelOf } from './engine';
import type { Plan, TechnicalPoint } from './types';

export function TechnicalWorkspace({ plan, geo, canWrite, onUpdate }: {
  plan: Plan; geo?: PlanGeoData; canWrite: boolean; onUpdate: (changes: Partial<Plan>) => void;
}) {
  const points = plan.technicalPoints ?? [];
  const pointsRef = useRef(points);
  pointsRef.current = points;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const [selectedId, setSelectedId] = useState('');
  const [area, setArea] = useState(false);
  const [zoneIds, setZoneIds] = useState<string[]>([]);
  const [previewZone, setPreviewZone] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const selected = points.find((point) => point.id === selectedId);
  const pending = points.filter((point) => point.status === 'pending');
  const zone = pending.filter((point) => zoneIds.includes(point.id));
  const safe = zone.filter((point) => !pointObstacles(point, points).length);
  const blocked = zone.filter((point) => pointObstacles(point, points).length);
  const changePoints = (next: TechnicalPoint[]) => { onUpdate({ technicalPoints: next }); setPreviewZone(false); setConfirmation(''); };
  const associate = (id: string) => {
    if (!selected || selected.status !== 'pending' || !canWrite) return;
    changePoints(points.map((point) => point.id === selected.id ? { ...point, buildingId: id || undefined } : point));
    setMessage('Association proposée : vérifiez puis validez ce point. Aucun local existant n’a été remplacé.');
  };
  const validate = (point: TechnicalPoint, confirmed = false) => {
    try {
      if (pointObstacles(point, points).length && !confirmed) { setConfirmation(point.id); return; }
      changePoints(validatePoint(points, point.id, confirmed));
      setMessage('Point validé. Les éventuels points concurrents restent à vérifier sans association ; l’ancien local remplacé est conservé dans l’historique.');
    } catch (error) { setMessage(error instanceof Error ? error.message : String(error)); }
  };
  const overlay: MapOverlay = {
    technicalPoints: points.filter((point) => point.status !== 'replaced'),
    focusedPointId: selected?.id,
    focusedBuildingId: selected?.buildingId,
    onPointClick: (id) => { setSelectedId(id); setConfirmation(''); },
    onBuildingClick: canWrite && selected?.status === 'pending' ? associate : undefined,
    areaMode: area && canWrite,
    onAreaError: setMessage,
    onAreaSelect: (selection) => {
      setZoneIds(selection.points); setPreviewZone(true); setArea(false); setConfirmation('');
      setMessage('Zone délimitée : vérifiez les cas validables et les obstacles avant de confirmer.');
    },
    legend: ['Orange : À vérifier · Vert : local validé · Rose : point actif.',
      area ? 'Dessinez la zone en maintenant le bouton gauche.' : 'Choisissez une pastille, puis son bâtiment. Au maximum un local validé par bâtiment.'],
  };
  const choices = geo?.buildings.features.map((feature) => {
    const id = String(feature.properties?.functionalId);
    return { value: id, label: labelOf(id) };
  }) ?? [];
  return <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
    <section style={{ flex: '0 0 48%', minWidth: 0, padding: 16, overflowY: 'auto', borderRight: `1px solid ${UMR.border}` }}>
      <UMFormStyles />
      <NJText as="h3" scale="md">Locaux techniques · vérification des points</NJText>
      <NJText scale="xs">SST du PROJET, communs à tous les plans, distincts des départs réseau. Un bâtiment peut avoir au maximum un local validé. Les conflits migrés conservent leur origine et statut initial ; aucune proposition géographique n’est une validation.</NJText>
      {canWrite && <>
        <FileImport id="technical-import" label="Importer des points GeoJSON" accept=".geojson,.json,application/geo+json,application/json"
          disabled={!geo || busy} onFile={async (file) => {
            if (!geo) return;
            const before = JSON.stringify(points);
            setBusy(true); setMessage(''); setPreviewZone(false); setConfirmation('');
            try {
              const imported = parseTechnicalPoints(await file.text(), file.name, geo);
              if (!mounted.current) return;
              if (JSON.stringify(pointsRef.current) !== before) throw new Error('Les locaux ont changé pendant la lecture. Réimportez le fichier pour ne pas écraser vos modifications.');
              onUpdate({ technicalPoints: [...points, ...imported] });
              setSelectedId(imported[0].id);
              setMessage(`${imported.length} points importés, tous À vérifier. Les positions dans un bâtiment sont seulement proposées.`);
            } catch (error) { setMessage(`Import refusé, aucune donnée modifiée : ${error instanceof Error ? error.message : String(error)}`); }
            finally { setBusy(false); }
          }} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <NJButton label="Charger les cas de démonstration" scale="sm" variant="secondary" disabled={!geo || busy}
            onClick={() => {
              if (!geo) return;
              try {
                const activeBuildings = new Set(points
                  .filter((point) => point.status !== 'replaced' && point.buildingId)
                  .map((point) => point.buildingId!));
                const demo = demoPoints(geo).map((point) => {
                  const next = activeBuildings.has(point.buildingId ?? '') ? { ...point, status: 'pending' as const } : point;
                  if (next.buildingId) activeBuildings.add(next.buildingId);
                  return next;
                });
                changePoints([...points, ...demo]);
                setSelectedId(demo.find((point) => point.status === 'pending')?.id ?? '');
              }
              catch (error) { setMessage(error instanceof Error ? error.message : String(error)); }
            }} />
          <NJButton label={area ? 'Annuler la zone' : 'Délimiter une zone à valider'} scale="sm" variant="secondary"
            disabled={!pending.length} onClick={() => { setArea(!area); setPreviewZone(false); }} />
        </div>
      </>}
      {busy && <NJText scale="xs" role="status">Lecture du fichier…</NJText>}
      {message && <NJText scale="xs" role="status">{message}</NJText>}
      <NJText scale="sm">{pending.length} À vérifier · {points.filter((point) => point.status === 'validated').length} validés · {points.filter((point) => point.status === 'replaced').length} remplacés</NJText>
      {previewZone && <section style={{ border: `1px solid ${UMR.border}`, padding: 12 }}>
        <NJText as="h4" scale="sm">Vérifier la zone avant validation</NJText>
        <NJText scale="sm">{safe.length} validables · {blocked.length} bloqués, dont {blocked.filter((point) => !point.buildingId).length} sans bâtiment.</NJText>
        {blocked.map((point) => <div key={point.id}>
          <NJButton label={`Résoudre ${point.id.slice(-8)}`} scale="sm" variant="secondary" onClick={() => { setSelectedId(point.id); setConfirmation(''); }} />
          <NJText scale="xs">{pointObstacles(point, points).join(' ')}</NJText>
        </div>)}
        <NJButton label={`Valider uniquement les ${safe.length} points sans conflit`} variant="primary" scale="sm"
          disabled={!canWrite || !safe.length} onClick={() => {
            try {
              const next = safe.reduce((all, point) => validatePoint(all, point.id, false), points);
              changePoints(next); setMessage(`${safe.length} points validés. ${blocked.length} points bloqués restent À vérifier : une décision individuelle est nécessaire.`);
            } catch (error) { setMessage(error instanceof Error ? error.message : String(error)); }
          }} />
        <NJButton label="Annuler la validation de zone" scale="sm" variant="secondary" onClick={() => setPreviewZone(false)} />
      </section>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 160, overflowY: 'auto' }}>
        {points.filter((point) => point.status !== 'replaced').map((point) => <NJButton key={point.id}
          label={`${point.status === 'validated' ? 'Validé' : 'À vérifier'} · ${point.id.slice(-8)}`}
          variant={point.id === selectedId ? 'primary' : 'secondary'} scale="sm" onClick={() => { setSelectedId(point.id); setConfirmation(''); }} />)}
      </div>
      {!points.length && <NJText scale="sm">Aucun point importé. Importez un GeoJSON ou chargez les cas de démonstration.</NJText>}
      {selected && <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <NJText as="h4" scale="sm">Point {selected.id.slice(-8)} · {selected.status === 'validated' ? 'Validé' : 'À vérifier'}</NJText>
        <NJText scale="xs">{selected.fileName} · {selected.coordinates.join(', ')} · {selected.originPlan ? `Origine : ${selected.originPlan} · statut initial ${selected.originalStatus}` : 'Origine : projet'}</NJText>
        <fieldset disabled={!canWrite || selected.status !== 'pending'} style={{ margin: 0, padding: 0, border: 0 }}>
          <UMSelect ariaLabel="Bâtiment du local technique" value={selected.buildingId ?? ''} onChange={associate}
            options={[{ value: '', label: 'Choisir un bâtiment ou cliquer sur la carte' }, ...choices]} />
        </fieldset>
        {selected.status === 'pending' && <>
          {pointObstacles(selected, points).map((reason) => <NJText key={reason} scale="xs" role="alert">{reason}</NJText>)}
          {canWrite && <NJButton label="Valider ce point" variant="primary" scale="sm" disabled={!selected.buildingId} onClick={() => validate(selected)} />}
        </>}
        {confirmation === selected.id && <div role="alert" style={{ border: `1px solid ${UMR.border}`, padding: 12 }}>
          <NJText scale="sm">{points.some((point) => point.id !== selected.id && point.status === 'validated' && point.buildingId === selected.buildingId)
            ? 'Remplacer le local existant ?' : 'Retenir ce point pour ce bâtiment ?'}</NJText>
          <NJText scale="xs">Ce point deviendra le seul local validé du bâtiment. Les autres candidats seront désassociés et resteront À vérifier. Un ancien local validé sera archivé comme remplacé.</NJText>
          <NJButton label="Confirmer ce point comme unique local" scale="sm" disabled={!canWrite} onClick={() => validate(selected, true)} />
          <NJButton label="Annuler et conserver l’existant" scale="sm" variant="secondary" onClick={() => setConfirmation('')} />
        </div>}
      </section>}
      <NJText scale="xs">Export GeoJSON et récapitulatif complet disponibles dans Import-export projet.</NJText>
    </section>
    {geo ? <MapPane geo={geo} overlay={overlay} /> : <NJText role="status">Chargement de la carte…</NJText>}
  </div>;
}
